import { HttpClient, HttpParams } from "@angular/common/http"
import { Injectable } from "@angular/core"
import { Observable, of, throwError } from "rxjs"
import { catchError, map, switchMap } from "rxjs/operators"
import { Talent } from "../models/talent.model"
import { AuthService } from "./auth.service"
import { environment } from "../../environments/environment"

interface TalentListResponse {
  status: string
  talents: Talent[]
  pagination?: {
    total: number
    limit: number
    offset: number
    has_more: boolean
  }
}

interface TalentSingleResponse {
  status: string
  talent: Talent
}

@Injectable({
  providedIn: "root",
})
export class TalentService {
  private apiUrl = `${environment.apiUrl}/talents`

  constructor(
    private http: HttpClient,
    private authService: AuthService,
  ) {}

  private authHeaders(): { Authorization: string } {
    return {
      Authorization: `Bearer ${this.authService.getIdToken()}`,
    }
  }

  private normalizeTalents(talents: Talent[] | undefined | null): Talent[] {
    return (talents || []).map((t) => ({
      ...t,
      id: t.id != null ? Number(t.id) : t.id,
    }))
  }

  /**
   * Full talent catalog for dropdowns / autocomplete (no 10-item default page).
   */
  getTalents(): Observable<Talent[]> {
    const params = new HttpParams().set("for_dropdown", "true").set("sort_by", "name").set("sort_order", "ASC")
    return this.http
      .get<TalentListResponse>(this.apiUrl, { headers: this.authHeaders(), params })
      .pipe(map((response) => this.normalizeTalents(response.talents)))
  }

  /**
   * Paginated list for management-style screens.
   */
  getTalentsPage(options?: {
    limit?: number
    offset?: number
    name?: string
    sort_by?: string
    sort_order?: "ASC" | "DESC"
  }): Observable<TalentListResponse> {
    let params = new HttpParams()
    const limit = options?.limit ?? 50
    const offset = options?.offset ?? 0
    params = params.set("limit", String(limit)).set("offset", String(offset))
    if (options?.name?.trim()) params = params.set("name", options.name.trim())
    if (options?.sort_by) params = params.set("sort_by", options.sort_by)
    if (options?.sort_order) params = params.set("sort_order", options.sort_order)

    return this.http.get<TalentListResponse>(this.apiUrl, { headers: this.authHeaders(), params }).pipe(
      map((response) => ({
        ...response,
        talents: this.normalizeTalents(response.talents),
      })),
    )
  }

  searchTalentsByName(name: string): Observable<Talent[]> {
    const params = new HttpParams()
      .set("name", name.trim())
      .set("for_dropdown", "true")
      .set("sort_by", "name")
    return this.http
      .get<TalentListResponse>(this.apiUrl, { headers: this.authHeaders(), params })
      .pipe(map((response) => this.normalizeTalents(response.talents)))
  }

  resolveOrCreateTalentByName(name: string): Observable<Talent> {
    const trimmed = name.trim()
    return this.searchTalentsByName(trimmed).pipe(
      switchMap((talents) => {
        const match = this.findTalentByName(talents, trimmed)
        if (match) return of(match)

        return this.createTalent({ name: trimmed, description: "Voice actor" }).pipe(
          catchError((err) => {
            if (this.isTalentAlreadyExistsError(err)) {
              return this.searchTalentsByName(trimmed).pipe(
                switchMap((retryTalents) => {
                  const retryMatch = this.findTalentByName(retryTalents, trimmed)
                  return retryMatch ? of(retryMatch) : throwError(() => err)
                }),
              )
            }
            return throwError(() => err)
          }),
        )
      }),
    )
  }

  private findTalentByName(talents: Talent[], name: string): Talent | undefined {
    const normalized = name.toLowerCase()
    return talents.find((t) => t.name.toLowerCase() === normalized)
  }

  private isTalentAlreadyExistsError(err: unknown): boolean {
    const error = err as { error?: { error?: string; message?: string }; message?: string }
    const msg = (error?.error?.error || error?.error?.message || error?.message || "").toString()
    const lower = msg.toLowerCase()
    return lower.includes("already exists") || lower.includes("talent with this name")
  }

  getTalent(id: number): Observable<Talent> {
    return this.http.get<TalentSingleResponse>(`${this.apiUrl}/${id}`, { headers: this.authHeaders() }).pipe(
      map((response) => {
        const talent = response.talent
        return { ...talent, id: talent?.id != null ? Number(talent.id) : talent?.id }
      }),
    )
  }

  createTalent(talent: Talent): Observable<Talent> {
    return this.http
      .post<TalentSingleResponse>(this.apiUrl, talent, { headers: this.authHeaders() })
      .pipe(map((response) => response.talent))
  }

  updateTalent(id: number, talent: Partial<Talent>): Observable<Talent> {
    return this.http
      .put<TalentSingleResponse>(`${this.apiUrl}/${id}`, talent, { headers: this.authHeaders() })
      .pipe(map((response) => response.talent))
  }

  deleteTalent(id: number): Observable<any> {
    return this.http.delete(`${this.apiUrl}/${id}`, { headers: this.authHeaders() })
  }
}
